import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowLeft, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Post-registration "check your email" landing.
 * Reached via state.email when Register completes.
 */
export default function CheckEmail({ email, emailSent = true }) {
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [autoResent, setAutoResent] = useState(false);

  const resend = async (silent = false) => {
    if (!email) return;
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-verification", { email });
      if (data?.email_sent === false) {
        if (!silent) toast.error("Re-send failed — our email provider is slow right now. Please try again in a minute.");
      } else if (!silent) {
        toast.success("Verification email re-sent. Check your inbox.");
      }
      setAutoResent(true);
    } catch (err) {
      if (!silent) toast.error(formatApiError(err.response?.data?.detail) || "Couldn't re-send");
    } finally {
      setResending(false);
    }
  };

  // If the initial send failed (transient Resend hiccup), silently retry
  // once after a 2-second backoff. The button is still there for the
  // user to fire manually if the auto-retry also misses.
  useEffect(() => {
    if (!emailSent && !autoResent) {
      const t = setTimeout(() => resend(true), 2000);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSent]);

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center" data-testid="check-email-card">
        <div className="w-14 h-14 rounded-full bg-emerald-50 grid place-items-center mx-auto">
          <Mail className="w-7 h-7 text-emerald-600" />
        </div>
        <h2 className="font-heading text-2xl font-semibold mt-4 text-slate-900">Verify your email</h2>
        <p className="text-sm text-slate-600 mt-2">
          We sent a verification link to <strong>{email || "your email"}</strong>. Click the link to activate your account — it expires in 24 hours.
        </p>
        {!emailSent && !autoResent && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-left text-xs text-amber-800" data-testid="check-email-resend-warning">
            <strong>Hmm, the first send was slow.</strong> We're auto-retrying — give it 30 seconds. If nothing arrives, hit "Re-send" below.
          </div>
        )}
        <p className="text-xs text-slate-400 mt-2">Can't find it? Check your spam folder.</p>
        <div className="mt-6 space-y-2">
          <Button
            type="button"
            onClick={() => resend(false)}
            disabled={resending || !email}
            variant="outline"
            className="w-full"
            data-testid="check-email-resend"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${resending ? "animate-spin" : ""}`} />
            Re-send verification email
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate("/login")}
            className="w-full"
            data-testid="check-email-back-login"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to sign in
          </Button>
        </div>
      </div>
    </div>
  );
}
